import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  Loader2,
  Coffee,
} from "lucide-react";
import api from "../lib/api";

const MAGNITUDE_RANK = {
  major: { label: "Major shift", weight: 3 },
  notable: { label: "Notable shift", weight: 2 },
  subtle: { label: "Subtle shift", weight: 1 },
};

/**
 * "What changed today" Dashboard feed — surfaces every watchlist symbol
 * whose composite sentiment shifted meaningfully day-over-day, ranked by
 * magnitude. Renders the Why-Now mini-summary inline so a single screen
 * answers "what should I look at first this morning?".
 */
export default function WhatsChangedFeed() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // symbol → bool

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api
      .get("/dashboard/whats-changed?limit=8")
      .then((r) => !cancel && setData(r.data))
      .catch(() => !cancel && setData(null))
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-6 flex items-center gap-2 text-sm text-[#6B7068]" data-testid="whats-changed-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Reading what changed today...
      </div>
    );
  }

  if (!data) return null;

  // Empty state — calm morning, no shifts in any watchlist symbol
  if (data.shifted === 0) {
    return (
      <div
        className="rounded-2xl bg-white border border-[#EAE6E1] p-6 flex items-start gap-4"
        data-testid="whats-changed-empty"
      >
        <div className="w-10 h-10 rounded-full bg-[#F9F8F6] flex items-center justify-center shrink-0">
          <Coffee className="w-5 h-5 text-[#6B7068]" strokeWidth={1.75} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] mb-0.5 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-[#D18E63]" strokeWidth={2.5} />
            What changed today
          </div>
          <h3 className="font-heading text-lg text-[#2A2F2B] leading-tight">
            A quiet morning across your watchlist
          </h3>
          <p className="text-sm text-[#6B7068] mt-1">
            None of your {data.evaluated} tracked symbols shifted meaningfully versus yesterday's sentiment.
            Great time to do nothing.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl bg-white border border-[#EAE6E1] p-6"
      data-testid="whats-changed-feed"
    >
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] mb-1 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-[#D18E63]" strokeWidth={2.5} />
            What changed today
          </div>
          <h2 className="font-heading text-2xl tracking-tight text-[#2A2F2B]">
            {data.shifted} of your {data.evaluated} symbols moved
          </h2>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">
          Ranked by magnitude
        </span>
      </div>
      <div className="space-y-3">
        {data.items.map((it) => (
          <ShiftRow
            key={`${it.asset_type}-${it.symbol}`}
            item={it}
            expanded={!!expanded[it.symbol]}
            onToggle={() => setExpanded({ ...expanded, [it.symbol]: !expanded[it.symbol] })}
          />
        ))}
      </div>
    </div>
  );
}

function ShiftRow({ item, expanded, onToggle }) {
  const isBullish = item.shift.kind === "bullish";
  const accent = isBullish ? "#5C8D73" : "#C66B59";
  const Arrow = isBullish ? TrendingUp : TrendingDown;
  const mag = MAGNITUDE_RANK[item.shift.magnitude] || { label: "Shift" };

  return (
    <div
      data-testid={`whats-changed-row-${item.symbol}`}
      className="rounded-xl border border-[#EAE6E1] hover:border-[#2A2F2B]/15 transition-soft"
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`whats-changed-toggle-${item.symbol}`}
        className="w-full px-4 py-3 flex items-center gap-3 text-left"
      >
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ background: accent }}
        >
          <Arrow className="w-4 h-4 text-white" strokeWidth={2.25} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to={`/asset/${item.asset_type}/${item.symbol}`}
              onClick={(e) => e.stopPropagation()}
              className="font-mono-ticker text-base font-semibold text-[#2A2F2B] hover:text-[#547A8F]"
            >
              {item.symbol}
            </Link>
            <span className="text-xs text-[#6B7068] truncate">{item.name}</span>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 text-[11px]">
            <span className="font-mono-ticker font-semibold uppercase tracking-[0.15em]" style={{ color: accent }}>
              {mag.label}
            </span>
            <span className="text-[#6B7068]">
              · {item.shift.prev_label} → {isBullish ? "Bullish" : "Bearish"}
            </span>
            <span className="font-mono-ticker text-[#2A2F2B]">
              ({item.shift.delta >= 0 ? "+" : ""}
              {item.shift.delta.toFixed(0)} pts)
            </span>
          </div>
        </div>
        <ChevronRight
          className={`w-4 h-4 text-[#6B7068] shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`}
          strokeWidth={1.75}
        />
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 ml-12" data-testid={`whats-changed-body-${item.symbol}`}>
          {item.summary ? (
            <p className="text-sm text-[#2A2F2B] leading-relaxed">{item.summary}</p>
          ) : (
            <p className="text-xs text-[#6B7068]">
              {item.shift.prev_label} → today's regime. Click {item.symbol} to dig into the breakdown.
            </p>
          )}
          {item.key_drivers && item.key_drivers.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-[#6B7068]">
              {item.key_drivers.map((d, i) => (
                <li key={`${item.symbol}-drv-${i}`} className="flex gap-2">
                  <span style={{ color: accent }} className="mt-0.5">▸</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
